// @mentions in a comment (Work screen, 2026-10-21) — the pure half. A mention
// is typed text in the body ("@Ana Holm") plus a typed ref the server stores
// and notifies ({type: 'person', id}; monad/notifications.py MENTION_TYPES).
import type { CommentMention } from '../api/client'

/** The `@query` being typed at the caret, or null when the caret is not in one.
 *  A mention query starts at an '@' that opens the text or follows whitespace. */
export function activeMentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret)
  const at = before.lastIndexOf('@')
  if (at < 0) return null
  if (at > 0 && !/\s/.test(before[at - 1])) return null
  const query = before.slice(at + 1)
  if (/[\n@]/.test(query) || query.length > 40) return null
  return { start: at, query }
}

/** Replace the active `@query` with `@Name ` and return the new text and caret. */
export function insertMention(text: string, start: number, caret: number, name: string): { text: string; caret: number } {
  const inserted = `@${name} `
  return { text: text.slice(0, start) + inserted + text.slice(caret), caret: start + inserted.length }
}

export interface PickedMention { id: string; name: string }

/** The mentions to send: each picked person whose `@Name` is still in the
 *  body, once (deleting the text drops the mention). */
export function mentionsInBody(body: string, picked: PickedMention[]): CommentMention[] {
  const seen = new Set<string>()
  const out: CommentMention[] = []
  for (const p of picked) {
    if (seen.has(p.id) || !body.includes(`@${p.name}`)) continue
    seen.add(p.id)
    out.push({ type: 'person', id: p.id })
  }
  return out
}

/** Split a body into text and `@Name` runs for the mentioned people, so the
 *  names render as mentions. Longest names first, so "@Ana Holm" wins over "@Ana". */
export function mentionRuns(body: string, names: string[]): Array<{ text: string; mention: boolean }> {
  const sorted = [...new Set(names.filter(Boolean))].sort((a, b) => b.length - a.length)
  if (sorted.length === 0) return [{ text: body, mention: false }]
  const escaped = sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const rx = new RegExp(`@(?:${escaped.join('|')})`, 'g')
  const out: Array<{ text: string; mention: boolean }> = []
  let last = 0
  for (const m of body.matchAll(rx)) {
    if (m.index! > last) out.push({ text: body.slice(last, m.index), mention: false })
    out.push({ text: m[0], mention: true })
    last = m.index! + m[0].length
  }
  if (last < body.length) out.push({ text: body.slice(last), mention: false })
  return out
}
